import { memo, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Space, Tag, Typography, message } from 'antd';
import _ from 'lodash';
import { dateToString, getDownloadUrl, getFullName } from '@/utils';
import { useGetChildInquiriesQuery } from '@/services/child-api';
import { DownloadSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import CompleteModal from './complete-modal';
import LettersModal from './letters-modal';

const { Text } = Typography;

const Inquiries = ({ childId, permissions }) => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const pageNumber = queryParams.get('page') || 1;
  const { data, isFetching, refetch } = useGetChildInquiriesQuery({ childId, pageNumber });
  const items = useMemo(() => data?.data.map((item) => _.omit(item, ['children'])), [data]);

  const [selectedItem1, setSelectedItem1] = useState();
  const [selectedItem2, setSelectedItem2] = useState();

  const handleDownloadClick = async () => {
    // Assuming getDownloadUrl() returns the URL for the download
    const url = getDownloadUrl(`/children/${childId}/inquiries/download/excel`);

    // Start the download
    window.open(url, '_blank');

    // Show the toast notification
    message.success('Completed!');
  };

  const columns = [
    {
      title: 'Parents',
      dataIndex: 'parents',
      render: (value) => (
        <Space direction="vertical" size={2}>
          <Link to={`/parent/${value.parentId}`}>
            {[getFullName(value.primaryName), getFullName(value.significantOtherName)].filter((s) => s).join(' & ')}
          </Link>
          {value.inactiveDate && (
            <Text type="danger" italic>
              Inactive
            </Text>
          )}
          {value.matchedDate && <Tag color="green">Matched</Tag>}
          {value.placementCapacityDate && <Tag color="warning">{value.placementCapacityNote}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'date',
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          {dateToString(value)}
          {record.fromPortal && <Tag color="processing">Portal Inquiry</Tag>}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.parents.homeStudy.status.name}
          {record.recommended && <i>Recommended</i>}
        </Space>
      ),
    },
    {
      title: 'Inquiry Status',
      key: 'inquiryStatus',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.parents.homeStudy.type.name}
          <i>{record.completedReason.name}</i>
        </Space>
      ),
    },
    {
      title: 'Out of State',
      dataIndex: 'outOfState',
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Letters',
      dataIndex: 'letters',
      render: (value) => value.length,
    },
  ];

  if (items?.length) {
    columns.push({
      title: (
        <Button type="primary" icon={<DownloadSvg fontSize="12" />} onClick={handleDownloadClick}>
          Download
        </Button>
      ),
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          {permissions.writeChild &&
            (record.isOpen ? (
              <Button type="link" onClick={() => setSelectedItem1(record)}>
                Complete
              </Button>
            ) : (
              <span style={{ opacity: 0.5 }}>Completed</span>
            ))}
          <Button type="link" onClick={() => setSelectedItem2(record)}>
            Letters
          </Button>
        </Space>
      ),
    });
  }

  return (
    <>
      <Table
        columns={columns}
        dataSource={items}
        rowKey="inquiryId"
        loading={isFetching}
        pageInfo={data}
        size="small"
      />
      <CompleteModal
        parentId={selectedItem1?.parents.parentId}
        inquiryId={selectedItem1?.inquiryId}
        refetch={refetch}
        onClose={() => setSelectedItem1()}
      />
      <LettersModal
        parentId={selectedItem2?.parents.parentId}
        inquiryId={selectedItem2?.inquiryId}
        onClose={() => setSelectedItem2()}
      />
    </>
  );
};

export default memo(Inquiries);
